/**
 * Brand Bible v2 fold-in — letter (A-I) ↔ BrandBiblePage section mapping.
 * Single source of truth for the jump-nav, the needs-attention queue's gap
 * deep-links, and any other place that needs to route a v2 section letter to
 * the legacy single-canvas section it lives in (contract Appendix A: gap ids
 * carry no `step`, so the letter prefix is the only signal — same rule used by
 * the wizard's StepReview, ported here for the fold-in canvas).
 *
 * The page renders one nav keyed by these letters — there is no second
 * numbering system and no separate letter strip. The spec's own grouping is
 * FACTS / VOICE / HYBRID (per-section retrieval lane), which is a P2 retrieval
 * concern, not navigation chrome, so it is deliberately not modelled here.
 */

export type SectionId =
  | 'essentials'
  | 'voice'
  | 'service'
  | 'proof'
  | 'audience'
  | 'competitors'
  | 'guardrails'
  | 'visual'
  | 'channels';

export type BbLetter = 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G' | 'H' | 'I';

export const BB_LETTERS: BbLetter[] = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I'];

export const BB_LETTER_LABEL: Record<BbLetter, string> = {
  A: 'Identity',
  B: 'Voice',
  C: 'Offers',
  D: 'Proof',
  E: 'Audience',
  F: 'Competitors',
  G: 'Compliance',
  H: 'Visual',
  I: 'Channels',
};


/**
 * One letter, one section — the page carries A-I. F (competitors) and I
 * (channel rules) each keep their own block, and no section answers to two
 * letters.
 */
export const BB_LETTER_TO_SECTION: Record<BbLetter, SectionId> = {
  A: 'essentials',
  B: 'voice',
  C: 'service',
  D: 'proof',
  E: 'audience',
  F: 'competitors',
  G: 'guardrails',
  H: 'visual',
  I: 'channels',
};

export const sectionForLetter = (letter: string): SectionId | null =>
  BB_LETTER_TO_SECTION[(letter?.[0]?.toUpperCase() ?? '') as BbLetter] ?? null;

/** Spec §3 names, used as the eyebrow above each section's friendly title. */
export const BB_LETTER_SPEC_TITLE: Record<BbLetter, string> = {
  A: 'Identity Card',
  B: 'Voice Profile',
  C: 'Offers & Pricing Facts',
  D: 'Proof Library',
  E: 'Market & Customer',
  F: 'Competitive Set',
  G: 'Compliance & Never List',
  H: 'Visual Identity',
  I: 'Channel Rules',
};

/** Gap/component ids are `${letter}${n}` (e.g. "A1", "I1") — contract Appendix A. */
export const sectionForScoreId = (id: string): SectionId | null => sectionForLetter(id);

/**
 * Which letter owns each entry table. Only `bb_chunks` rows carry a `section`
 * column of their own — every other table belongs to a fixed section, so
 * reading `entry.section` alone (as the attention queue used to) leaves most
 * rows with no section and therefore no working deep-link.
 */
const TABLE_LETTER: Record<string, BbLetter> = {
  identity: 'A',
  locations: 'A',
  credentials: 'A', // license/insurance are §A; cert/award are §D — see below
  voice_profile: 'B',
  services: 'C',
  offers: 'C',
  review_stats: 'D',
  case_studies: 'D',
  compliance_rules: 'G',
  visual_identity: 'H',
};

/** §D credential kinds — the same rows the §D coverage component counts. */
const PROOF_CREDENTIAL_KINDS = new Set(['certification', 'award']);

/** Where an entry should scroll to. Handles chunks (own letter) and credentials (split A/D). */
export function sectionForEntry(
  table: string,
  entry?: { section?: string | null; kind?: string | null } | null,
): SectionId | null {
  if (table === 'chunks') return entry?.section ? sectionForLetter(entry.section) : null;
  if (table === 'credentials' && entry?.kind && PROOF_CREDENTIAL_KINDS.has(entry.kind)) {
    return 'proof';
  }
  const letter = TABLE_LETTER[table];
  return letter ? BB_LETTER_TO_SECTION[letter] : null;
}

/**
 * Chunk-shaped drafts for ONE legacy section — the fix for BB-DRAFTS-BUCKETING
 * (a static `['chunks']` table bucket pulled every section's chunks into
 * whichever section's wall rendered first, because `draftRowsForTables` only
 * filtered by table name, not by the row's own `section`). Routes each row by
 * `sectionForEntry`, so a §E audience chunk (say) never surfaces as a §B
 * Voice draft just because both are stored in `bb_chunks`.
 */
export function chunkDraftsForSection<
  T extends { table: string; entry: { section?: string | null; kind?: string | null } },
>(rows: T[], section: SectionId): T[] {
  return rows.filter((r) => sectionForEntry(r.table, r.entry) === section);
}

/** Points a section has earned out of what it can earn, rolled up from the score. */
export interface SectionRollup {
  points: number;
  max: number;
}

/**
 * Roll `/brand-bible/v2/score` components up per legacy section so the rail can
 * state one truth. Components are keyed `${letter}${n?}` (contract Appendix A) —
 * a section owns every component whose letter maps to it.
 *
 * Every retained letter has a section, so the rollup accounts for all 93 A-I
 * points.
 */
export function sectionRollups(
  components: { id: string; points: number; max: number; label?: string }[] | undefined | null,
): Record<SectionId, SectionRollup> {
  const out = {} as Record<SectionId, SectionRollup>;
  for (const letter of BB_LETTERS) {
    const section = BB_LETTER_TO_SECTION[letter];
    if (!out[section]) out[section] = { points: 0, max: 0 };
  }
  for (const c of components ?? []) {
    const section = sectionForScoreId(c.id ?? '');
    if (!section) continue;
    out[section].points += c.points ?? 0;
    out[section].max += c.max ?? 0;
  }
  // Fractional coverage components (e.g. A1 = filled ÷ 7) would otherwise render
  // as "6.86 of 20 pts"; the rail shows whole points, the score itself is exact.
  for (const section of Object.keys(out) as SectionId[]) {
    out[section].points = Math.round(out[section].points);
    out[section].max = Math.round(out[section].max);
  }
  return out;
}
